import { gsap } from 'gsap';
import { Container, Sprite } from '../../odie';
import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { ParticleEmitter } from '../particles/ParticleEmitter';
import { config } from '../defs/config';
import { getMatch2AssetPath } from '../util/assets';
import { PositionType } from '../../lib/defs/types';
import { mapFromViewPosition } from '../util/mapTools';

const manifest = {
  airplane: 'fx.bullet/fx.bullet.airplane.png',
  smoke: 'fx.bullet/fx.bullet.smoke.png',
};

export class BulletAnimation extends Container implements IAnimation {
  private _airplane!: Sprite;
  private _smokeEmitter!: ParticleEmitter;
  private _position: PositionType;
  public onMove?: (row: number) => void;

  public static assets(): string[] {
    return [manifest.airplane, manifest.smoke];
  }

  constructor(position: PositionType) {
    super();
    this._position = position;
  }

  public async start(): Promise<void> {
    app.sound.play('puzzle-bullet');
    this._smokeEmitter = this._createSmokeEmitter();
    this.addChild(this._smokeEmitter.view);
    this._smokeEmitter.start();
    this._airplane = Sprite.from(manifest.airplane);
    this._airplane.anchorX = this._airplane.anchorY = 0.5;
    this.addChild(this._airplane);
    this.x = -100;
    this.y = this._position.y + 35;
    const destination = 1200;
    await gsap.to(this, {
      x: destination,
      duration: 2,
      ease: 'linear',
      onUpdate: () => this._onUpdate(),
    });
  }

  public stop() {
    this._smokeEmitter.stop();
    this.removeChild(this._smokeEmitter.view);
    this.removeChild(this._airplane);
  }

  private _createSmokeEmitter(): ParticleEmitter {
    const emitter = app.particles.create({
      textures: [getMatch2AssetPath(manifest.smoke)],
      rate: 0.05,
      limit: 64,
      duration: 1,
      velocity: { x: -config.sim.rocket.speed * 0.7, y: 0 },
      scale: [
        { x: 0.5, y: 0.5 },
        { x: 1, y: 1 },
      ],
      behaviors: [
        { type: 'explode', magnitude: [16, 32] },
        { type: 'scale', from: 1.2, to: 0.5 },
        { type: 'fade', from: 1, to: 0 },
        { type: 'kinematic' },
      ],
    });
    return emitter;
  }

  private _onUpdate() {
    const mapPos = mapFromViewPosition({
      x: this.x - 400, // roughly thalf screen position offset
      y: this.y,
    });
    this.onMove?.(mapPos.x);

    this._airplane.y = Math.sin(this.x / 100) * 10;
  }
}
